import React from 'react';
import {
  Box,
  Stack,
  IconButton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider,
  Tooltip,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';

// Check-type icons
import SensorsIcon from '@mui/icons-material/Sensors';
import RouterIcon from '@mui/icons-material/Router';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import SpeedIcon from '@mui/icons-material/Speed';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';

// Status icons
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import QuestionMarkRoundedIcon from '@mui/icons-material/QuestionMarkRounded';

import {
  getQualityOfServiceCheck,
  getComfortHealthCheck,
  getFrostProtectionCheck,
  getGatewayHealthCheck,
  getInitializationCheck,
  getMachineLearningCheck,
  getSensorHealthCheck,
  getSystemPerformanceCheck,
  getTargetTemperatureCheck,
} from '@/features/building-optimization-operations-dashboard/utils/utilityFunctions';

const SEVERITY_ORDER = ['Unknown', 'Success', 'Warning', 'Error', 'Critical'];

const getStatusIcon = (status) => {
  switch (status) {
    case 'Error':
      return <ErrorRoundedIcon color="error" />;
    case 'Warning':
      return <WarningRoundedIcon color="warning" />;
    case 'Success':
      return <CheckCircleRoundedIcon color="success" />;
    case 'Critical':
      return <PriorityHighRoundedIcon color="error" />;
    case 'Unknown':
      return <QuestionMarkRoundedIcon color="gray" />;
    default:
      return null;
  }
};

function normalizeStatus(raw) {
  if (!raw || typeof raw !== 'string') return 'Unknown';
  const s = raw.trim().toLowerCase();
  if (s.startsWith('succ')) return 'Success';
  if (s.startsWith('warn')) return 'Warning';
  if (s === 'error' || s === 'err') return 'Error';
  if (s.startsWith('crit')) return 'Critical';
  if (s.startsWith('unk')) return 'Unknown';
  return 'Unknown';
}

function worstStatus(statuses) {
  let worst = 'Unknown';
  for (const s of statuses) {
    const norm = normalizeStatus(s);
    if (SEVERITY_ORDER.indexOf(norm) > SEVERITY_ORDER.indexOf(worst)) {
      worst = norm;
    }
  }
  return worst;
}

function collectStatuses(result) {
  const out = [];
  if (!result) return out;

  if (result.Status) out.push(result.Status);
  if (result.status) out.push(result.status);
  if (result.OverallStatus) out.push(result.OverallStatus);
  if (result.overallStatus) out.push(result.overallStatus);

  if (Array.isArray(result.checks)) {
    for (const c of result.checks) {
      if (c?.Status) out.push(c.Status);
      if (c?.status) out.push(c.status);
      if (Array.isArray(c?.subChecks)) {
        for (const sc of c.subChecks) {
          if (sc?.Status) out.push(sc.Status);
          if (sc?.status) out.push(sc.status);
        }
      }
    }
  }
  return out;
}

function hasAnyMessages(result) {
  if (!result) return false;
  if (Array.isArray(result.messages) && result.messages.length) return true;
  if (Array.isArray(result.checks)) {
    for (const c of result.checks) {
      if (Array.isArray(c?.messages) && c.messages.length) return true;
      if (Array.isArray(c?.subChecks)) {
        for (const sc of c.subChecks) {
          if (Array.isArray(sc?.messages) && sc.messages.length) return true;
        }
      }
    }
  }
  return false;
}

// map of checks → icon + getter
const CHECKS = [
  {
    key: 'SensorHealthCheck',
    label: 'Sensors',
    Icon: SpeedIcon,
    getter: getSensorHealthCheck,
  },
  {
    key: 'GatewayHealthCheck',
    label: 'Gateway',
    Icon: RouterIcon,
    getter: getGatewayHealthCheck,
  },
  {
    key: 'MachineLearning',
    label: 'Machine Learning',
    Icon: PsychologyIcon,
    getter: getMachineLearningCheck,
  },
  {
    key: 'TargetTemperatureStatus',
    label: 'Target Temperature',
    Icon: ThermostatIcon,
    getter: getTargetTemperatureCheck,
  },
  {
    key: 'SystemPerformance',
    label: 'System Performance',
    Icon: SettingsRoundedIcon,
    getter: getSystemPerformanceCheck,
  },
  {
    key: 'FP',
    label: 'Frost Protection',
    Icon: AcUnitIcon,
    getter: getFrostProtectionCheck,
  },
  {
    key: 'Initialization',
    label: 'Initialization',
    Icon: PlayCircleFilledIcon,
    getter: getInitializationCheck,
  },
  {
    key: 'ComfortHealthCheck',
    label: 'Comfort Health Check',
    Icon: () => (
      <i className="fa-solid fa-person-shelter" style={{ padding: 3 }} />
    ), // ✅ works like a component
    getter: getComfortHealthCheck,
  },
  {
    key: 'QualityOfService',
    label: 'Quality of Service',
    Icon: SignalCellularAltIcon,
    getter: getQualityOfServiceCheck,
  },
];

// flatten messages from {messages, checks[], subChecks[].messages}
function collectMessages(result) {
  if (!result) return [];
  const msgs = [];
  if (Array.isArray(result.messages)) msgs.push(...result.messages);
  if (Array.isArray(result.checks)) {
    for (const c of result.checks) {
      if (Array.isArray(c?.messages)) msgs.push(...c.messages);
      if (Array.isArray(c?.subChecks)) {
        for (const sc of c.subChecks) {
          if (Array.isArray(sc?.messages)) msgs.push(...sc.messages);
        }
      }
    }
  }
  return msgs;
}

// derive a normalized status and a matching status icon + palette color
function getStatusVisual(result) {
  const statuses = collectStatuses(result);
  let status = statuses.length ? worstStatus(statuses) : 'Unknown';
  if (status === 'Unknown') {
    status = hasAnyMessages(result) ? 'Warning' : 'Success';
  }

  switch (status) {
    case 'Success':
      return { status, Color: 'success', StatusIcon: CheckCircleRoundedIcon };
    case 'Warning':
      return { status, Color: 'warning', StatusIcon: WarningRoundedIcon };
    case 'Error':
      return { status, Color: 'error', StatusIcon: ErrorRoundedIcon };
    case 'Critical':
      return { status, Color: 'error', StatusIcon: PriorityHighRoundedIcon };
    default:
      return {
        status: 'Unknown',
        Color: 'default',
        StatusIcon: HelpOutlineRoundedIcon,
      };
  }
}
function SubscriptionStatusCell({
  state,
  hideSuccess = true, // <- set to false to show everything
  size = 'small', // IconButton size
  spacing = 1.0, // gap between icons (MUI spacing units)
}) {
  const [openKey, setOpenKey] = React.useState(null);

  if (!state) return null;

  // build list of available checks
  const evaluated = CHECKS.map(({ key, label, Icon, getter }) => {
    const data = getter?.(state);
    if (!data) return null;
    const visual = getStatusVisual(data);
    return { key, label, Icon, data, visual };
  }).filter(Boolean);

  // optionally hide good ones
  const visible = hideSuccess
    ? evaluated.filter(({ visual }) => visual.status !== 'Success')
    : evaluated;

  if (visible.length === 0) return null;

  return (
    <>
      {/* icon row */}
      <Stack direction="row" spacing={spacing} alignItems="center">
        {visible.map(({ key, label, Icon, visual }) => {
          // <-- use visible
          const { StatusIcon, Color } = visual;
          return (
            <Tooltip title={label} key={key} arrow placement="top">
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <StatusIcon
                    sx={{
                      fontSize: 14,
                      color: (theme) =>
                        Color === 'default'
                          ? theme.palette.text.disabled
                          : theme.palette[Color].main,
                    }}
                  />
                }
              >
                <IconButton
                  size={size}
                  onClick={() => setOpenKey(key)}
                  sx={{ border: 1, borderColor: 'divider' }}
                  aria-label={key}
                >
                  <Icon />
                </IconButton>
              </Badge>
            </Tooltip>
          );
        })}
      </Stack>

      {/* dialogs */}
      {visible.map(({ key, label, data, visual }) => {
        const messages = collectMessages(data);
        const open = openKey === key;

        // derive a structured list for display (checks + subChecks)
        const items = [];
        if (Array.isArray(data.checks)) {
          data.checks.forEach((c, idx) => {
            items.push({
              title: c?.name || `Check #${idx + 1}`,
              icon: c?.icon || null,
              messages: [
                ...(Array.isArray(c?.messages) ? c.messages : []),
                ...(Array.isArray(c?.subChecks)
                  ? c.subChecks.flatMap((sc) => sc?.messages || [])
                  : []),
              ],
            });
          });
        } else if (messages.length) {
          items.push({ title: 'Messages', messages });
        }

        return (
          <Dialog
            key={key}
            open={open}
            onClose={() => setOpenKey(null)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              <ListItem disableGutters disablePadding>
                <ListItemIcon>{getStatusIcon(visual.status)}</ListItemIcon>
                <ListItemText primary={label} />
              </ListItem>
            </DialogTitle>
            <DialogContent dividers>
              {items.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No details available.
                </Typography>
              )}

              {items.map((section, i) => (
                <Box key={i} sx={{ mb: i === items.length - 1 ? 0 : 2 }}>
                  <ListItem disableGutters disablePadding>
                    <ListItemIcon>{section.icon}</ListItemIcon>
                    <ListItemText primary={section.title} />
                  </ListItem>
                  {section.messages.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No messages.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'grid', gap: 0.75 }}>
                      {section.messages.map((m, j) => (
                        // messages can be string or JSX per your helpers
                        <Box key={j} sx={{ fontSize: 14, lineHeight: 1.35 }}>
                          {m}
                        </Box>
                      ))}
                    </Box>
                  )}
                  {i < items.length - 1 && <Divider sx={{ mt: 2 }} />}
                </Box>
              ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenKey(null)}>Close</Button>
            </DialogActions>
          </Dialog>
        );
      })}
    </>
  );
}

export default SubscriptionStatusCell;
